import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';
import { clearOfflineAttachments } from './attachments';

const DAY = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Serve cached data immediately, refresh in the background, and never block on the network.
      networkMode: 'offlineFirst',
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * DAY,
      retry: (count, error) => count < 2 && !/permission|JWT|not found/i.test(String(error)),
      refetchOnWindowFocus: true,
    },
    mutations: { networkMode: 'online' },
  },
});

/** Query cache persisted to IndexedDB so protocols and medications remain available offline. */
export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => get<string>(key).then((v) => v ?? null),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: 'ems-query-cache',
  throttleTime: 2000,
});

export const PERSIST_MAX_AGE = 30 * DAY;

/** Only persist clinical reference data and the user's own records — not admin reports. */
export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  const root = String(queryKey[0]);
  return !root.startsWith('admin');
}

export async function clearOfflineData() {
  queryClient.clear();
  await persister.removeClient();
  await clearOfflineAttachments();
}
